/**
 * Durability layer for the telemetry client. Every event is persisted to the
 * outbox the moment it is enqueued; on a successful flush the rows are
 * deleted. If the page dies (Outlook crash, host kill, network drop) before
 * a flush completes, the rows survive and replay on the next add-in load.
 *
 * Two implementations:
 *   - {@link IndexedDBOutbox} — production. Durable across process restarts.
 *   - {@link InMemoryOutbox}  — fallback when IndexedDB is unavailable
 *                              (legacy webview, locked-down storage policy),
 *                              and the harness for unit tests.
 *
 * The `Outbox` interface is the contract — every consumer depends on it,
 * never on a concrete class. SOLID: dependency inversion in service of
 * testability and graceful degradation.
 */

import type { TelemetryEvent } from "./types";

/** Hard ceiling — older events drop when full. Matches server-side queue cap. */
export const OUTBOX_MAX_SIZE = 5_000;

/** Public contract every outbox satisfies. */
export interface Outbox {
  /** Persist one event. Idempotent on duplicate `eventId`. */
  put(event: TelemetryEvent): Promise<void>;

  /** Remove the listed events. Missing ids are ignored. */
  deleteByIds(eventIds: readonly string[]): Promise<void>;

  /** Return every persisted event (chronological order if implementation allows). */
  drain(): Promise<TelemetryEvent[]>;

  /** Current count of persisted events. */
  size(): Promise<number>;

  /** Remove everything. Used by tests; never called in production. */
  clear(): Promise<void>;
}

/**
 * In-memory outbox backed by a `Map`. Ordered by insertion (which matches
 * `occurredAtUtc` order in practice). Drop-oldest when full.
 *
 * Why this isn't a test-only artefact: a runtime that doesn't expose
 * `indexedDB` (or where `indexedDB.open` rejects for policy reasons) still
 * needs SOME outbox so the client logic doesn't have to branch. This class
 * is that fallback.
 */
export class InMemoryOutbox implements Outbox {
  private readonly store = new Map<string, TelemetryEvent>();

  constructor(private readonly maxSize: number = OUTBOX_MAX_SIZE) {}

  put(event: TelemetryEvent): Promise<void> {
    if (this.store.size >= this.maxSize && !this.store.has(event.eventId)) {
      // Drop oldest. Map preserves insertion order so the first key is the
      // oldest entry — same drop-oldest semantics as the server-side
      // PeriodicBatchingSink queue.
      const oldestKey = this.store.keys().next().value;
      if (oldestKey !== undefined) {
        this.store.delete(oldestKey);
      }
    }
    this.store.set(event.eventId, event);
    return Promise.resolve();
  }

  deleteByIds(eventIds: readonly string[]): Promise<void> {
    for (const id of eventIds) {
      this.store.delete(id);
    }
    return Promise.resolve();
  }

  drain(): Promise<TelemetryEvent[]> {
    return Promise.resolve(Array.from(this.store.values()));
  }

  size(): Promise<number> {
    return Promise.resolve(this.store.size);
  }

  clear(): Promise<void> {
    this.store.clear();
    return Promise.resolve();
  }
}

/**
 * IndexedDB-backed outbox. The schema is intentionally minimal — a single
 * object store keyed by `eventId`, with an index on `occurredAtUtc` so we
 * can drop the oldest events when the cap is hit.
 *
 * Failure mode: if any IDB operation rejects, the caller sees a failed
 * promise. The {@link TelemetryClient} catches and degrades gracefully so a
 * misbehaving IDB doesn't crash the add-in.
 */
export class IndexedDBOutbox implements Outbox {
  private static readonly DB_NAME = "aiva-telemetry";
  private static readonly DB_VERSION = 1;
  private static readonly STORE = "outbox";
  private static readonly INDEX_BY_TIME = "occurredAtUtc";

  private dbPromise: Promise<IDBDatabase> | undefined;

  constructor(
    private readonly factory: IDBFactory,
    private readonly maxSize: number = OUTBOX_MAX_SIZE,
  ) {}

  async put(event: TelemetryEvent): Promise<void> {
    const db = await this.openDb();
    return this.awaitRequest(() => {
      const tx = db.transaction(IndexedDBOutbox.STORE, "readwrite");
      tx.objectStore(IndexedDBOutbox.STORE).put(event);
      return tx;
    }).then(() => this.enforceCap(db));
  }

  async deleteByIds(eventIds: readonly string[]): Promise<void> {
    if (eventIds.length === 0) return;
    const db = await this.openDb();
    return this.awaitRequest(() => {
      const tx = db.transaction(IndexedDBOutbox.STORE, "readwrite");
      const store = tx.objectStore(IndexedDBOutbox.STORE);
      for (const id of eventIds) {
        store.delete(id);
      }
      return tx;
    });
  }

  async drain(): Promise<TelemetryEvent[]> {
    const db = await this.openDb();
    return new Promise<TelemetryEvent[]>((resolve, reject) => {
      const tx = db.transaction(IndexedDBOutbox.STORE, "readonly");
      const req = tx.objectStore(IndexedDBOutbox.STORE).getAll();
      req.onsuccess = () => resolve(req.result as TelemetryEvent[]);
      req.onerror = () => reject(asError(req.error, "IndexedDBOutbox.drain failed"));
    });
  }

  async size(): Promise<number> {
    const db = await this.openDb();
    return new Promise<number>((resolve, reject) => {
      const tx = db.transaction(IndexedDBOutbox.STORE, "readonly");
      const req = tx.objectStore(IndexedDBOutbox.STORE).count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(asError(req.error, "IndexedDBOutbox.size failed"));
    });
  }

  async clear(): Promise<void> {
    const db = await this.openDb();
    return this.awaitRequest(() => {
      const tx = db.transaction(IndexedDBOutbox.STORE, "readwrite");
      tx.objectStore(IndexedDBOutbox.STORE).clear();
      return tx;
    });
  }

  /** Lazily open (and cache) the database connection. */
  private openDb(): Promise<IDBDatabase> {
    this.dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = this.factory.open(IndexedDBOutbox.DB_NAME, IndexedDBOutbox.DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IndexedDBOutbox.STORE)) {
          const store = db.createObjectStore(IndexedDBOutbox.STORE, { keyPath: "eventId" });
          store.createIndex(IndexedDBOutbox.INDEX_BY_TIME, "occurredAtUtc", { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(asError(req.error, "IndexedDBOutbox.openDb failed"));
    });
    return this.dbPromise;
  }

  /** Wrap an IDB transaction in a promise that resolves on `complete`. */
  private awaitRequest(start: () => IDBTransaction): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const tx = start();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(asError(tx.error, "IndexedDBOutbox: transaction failed"));
      tx.onabort = () => reject(asError(tx.error, "IndexedDBOutbox: transaction aborted"));
    });
  }

  /**
   * If over cap, delete oldest rows until back to limit. Implemented with a
   * cursor on the `occurredAtUtc` index so we don't have to load every row
   * into memory just to find the oldest ones.
   */
  private async enforceCap(db: IDBDatabase): Promise<void> {
    const current = await this.size();
    if (current <= this.maxSize) return;

    const overflow = current - this.maxSize;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IndexedDBOutbox.STORE, "readwrite");
      const store = tx.objectStore(IndexedDBOutbox.STORE);
      const index = store.index(IndexedDBOutbox.INDEX_BY_TIME);
      let deleted = 0;
      const req = index.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor && deleted < overflow) {
          cursor.delete();
          deleted++;
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(asError(tx.error, "IndexedDBOutbox.enforceCap failed"));
    });
  }
}

/**
 * Coerce an IndexedDB error (typed `DOMException | null`) into a real
 * `Error` so Promise rejections always carry an actual Error instance.
 * SonarQube flags `reject(domException | null)` as a smell because most
 * Promise-handling code assumes the reason is an Error.
 *
 * `DOMException` already extends `Error` in modern lib types, so when it's
 * non-null we just pass it through; otherwise we synthesise one from the
 * fallback message.
 */
function asError(source: DOMException | null, fallbackMessage: string): Error {
  return source instanceof Error ? source : new Error(fallbackMessage);
}

/**
 * Return the production outbox: IndexedDB when available, in-memory
 * otherwise. The fallback is silent — operators only learn about it via the
 * absence of replayed events on next boot.
 */
export function createOutbox(): Outbox {
  // Direct === comparison instead of `typeof X === "undefined"` (Sonar
  // S6644: `typeof undefined` is an older idiom kept only for hostile-runtime
  // safety, which we don't need here — `indexedDB` is always a declared
  // property on globalThis, just sometimes set to undefined).
  if (globalThis.indexedDB === undefined) {
    return new InMemoryOutbox();
  }
  return new IndexedDBOutbox(globalThis.indexedDB);
}
